import { useRef, useEffect } from 'react';
import { Command, CommandItem, CommandList } from '@reflex/ui/components/ui/command';
import { cn } from '@reflex/ui/lib/utils';
import type { AvailableCommand } from './sdk-chat/types.js';

interface CommandPickerProps {
  commands: AvailableCommand[];
  selectedIndex: number;
  onSelect: (cmd: AvailableCommand) => void;
  onHover: (index: number) => void;
  style?: React.CSSProperties;
}

// Filtering is off: ChatInput already filters and drives selection via selectedIndex / keyboard.
export function CommandPicker({
  commands,
  selectedIndex,
  onSelect,
  onHover,
  style,
}: CommandPickerProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const selected = commands[selectedIndex];
  const value = selected?.name ?? '';

  useEffect(() => {
    const items = listRef.current?.querySelectorAll<HTMLElement>('[cmdk-item]');
    items?.[selectedIndex]?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (commands.length === 0) return null;

  return (
    <Command
      shouldFilter={false}
      value={value}
      onValueChange={(next) => {
        const idx = commands.findIndex((c) => c.name === next);
        if (idx >= 0) onHover(idx);
      }}
      style={style}
      className={cn(
        'h-auto overflow-hidden rounded-xl border border-border/60 bg-popover/95 text-popover-foreground shadow-lg backdrop-blur-md',
        'supports-[backdrop-filter]:bg-popover/80',
      )}
    >
      <CommandList
        ref={listRef}
        label="Slash commands"
        className="max-h-80 scroll-py-1 overflow-y-auto p-1.5"
      >
        {commands.map((cmd) => (
          <CommandItem
            key={cmd.name}
            value={cmd.name}
            onSelect={() => onSelect(cmd)}
            onMouseDown={(e) => {
              // Keep the composer focused so typing continues after pick.
              e.preventDefault();
            }}
            className="group flex cursor-default flex-col items-stretch gap-0.5 rounded-lg px-2.5 py-1.5"
          >
            <span className="flex min-w-0 items-baseline gap-1.5">
              <span className="min-w-0 truncate font-mono text-sm font-medium text-primary">
                /{cmd.name}
              </span>
              {cmd.input && (
                <span
                  aria-hidden
                  className="shrink-0 whitespace-nowrap font-mono text-xs text-muted-foreground/70"
                >
                  &lt;value&gt;
                </span>
              )}
            </span>
            {cmd.description && (
              <span className="line-clamp-2 text-xs leading-snug text-muted-foreground group-data-[selected=true]:text-accent-foreground/75">
                {cmd.description}
              </span>
            )}
          </CommandItem>
        ))}
      </CommandList>
    </Command>
  );
}
